import {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {useDebouncedPrimitiveValue} from "../../../hooks/useDebouncedPrimitiveValue";
import {DASHBOARD_SEARCH_DEBOUNCE_MS} from "../utils/constants";

/**
 * Dashboard search state and its `?q=` round trip. `searchInput` is what the
 * user sees; `searchTerm` is the debounced value the columns query on.
 */
export const useDashboardSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Read at effect time without depending on location, which would write the
  // outgoing term back over an external URL change. Same role as v5's
  // `history.location`.
  const locationRef = useRef(location);
  locationRef.current = location;

  const [searchInput, setSearchInput] = useState(
    () => new URLSearchParams(location.search).get("q") ?? "",
  );
  const searchTerm = useDebouncedPrimitiveValue(
    searchInput,
    DASHBOARD_SEARCH_DEBOUNCE_MS,
  );

  // Replace rather than push: typing should not fill history. Keyed on
  // `searchTerm` alone, because depending on the location too would make an
  // external URL change immediately write the old term back over it.
  useEffect(() => {
    const {pathname, search} = locationRef.current;
    const shared = searchTerm.trim();
    const params = new URLSearchParams(search);
    if ((params.get("q") ?? "") === shared) {
      return;
    }
    if (shared) {
      params.set("q", shared);
    } else {
      params.delete("q");
    }
    const next = params.toString();
    navigate(`${pathname}${next ? `?${next}` : ""}`, {replace: true});
  }, [searchTerm, navigate]);

  // Adopt a `?q=` this hook did not write, such as a sidebar link back to an
  // unfiltered dashboard while the page stays mounted. Compared trimmed, because
  // the URL carries the trimmed term: without that, typing a trailing space would
  // see our own write come back and strip it mid-word.
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get("q") ?? "";
    setSearchInput(current => (current.trim() === fromUrl ? current : fromUrl));
  }, [location.search]);

  return {searchInput, setSearchInput, searchTerm};
};
